import { Answer, DocumentVerwijzing } from './answer.js'
import type { Definitie } from './definitie.js'
import type { Field, RowField } from './field.js'
import { fitsFileKind, isFileKind } from './fileKind.js'
import { datatypeOf } from './formXml.js'
import type { Moment, Person } from './moment.js'
import { type StoredFile, checkFigures, importRichText, plainTextOfStructure, withFigureSizes } from './richTextImport.js'

/**
 * Een correctie door het processysteem op een stand van een eigen blok (`docs/koppelvlak.md`
 * hoofdstuk 4, R-201, besluit van 23 oktober 2026).
 *
 * Het processysteem geeft een correctie expliciet mee; dit product neemt de waarde over in de stand
 * die het formulier toont, op naam van het processysteem en met het moment van het verzoek. De
 * revisie zelf verandert niet: wat er werd ingediend, blijft wat er werd ingediend (R-95).
 *
 * **Rich text is HTML**, en gaat door dezelfde omzetting als een rich-text-veld uit het dossier
 * (`importRichText`): wat de editor niet kan dragen wordt geweigerd en niet uitgepakt, en een figuur
 * moet als afbeelding in de opslag van dit product staan. Die opslag kent dit pakket niet; de
 * aanroeper zoekt de figuren op (`correctionFigures`) en geeft mee wat hij vond. De platte tekst naast
 * de structuur is die welke de pod uit dezelfde structuur leest (`plainTextOfStructure`).
 *
 * **Een herhaalgroep per cel.** Een correctie noemt de rij met haar `rij`, het rij-id zoals het bericht
 * haar noemt, en de kolom als `veld`. Alleen die cel verandert; de andere cellen houden hun waarde en
 * hun herkomst. Een rij toevoegen of weghalen kan een correctie niet: een rij-id maakt dit product zelf,
 * en een rij die verdwijnt neemt de verwijzingen ernaar mee.
 *
 * **Een bijlage is een verwijzing naar een bestand dat er al is** (U25, R-199, R-104). Het processysteem
 * levert het bestand eerst aan voor het dossier (`POST /bijlagen`), en daar controleert de opslagdienst
 * de inhoud tegen de vaste lijst van soorten (`FILE_KINDS`). De correctie noemt daarna alleen de hash en
 * de bestandsnaam, en vervangt de hele lijst van het veld. Wat de opslag van elk bestand weet, en de
 * eisen van het veld, geeft de aanroeper mee (`AttachmentCheck`): dit pakket kent de opslag niet en de
 * regel `bestandseisen` rekent de engine uit. Zonder die twee wordt een bijlage geweigerd, en niet
 * zonder controle overgenomen.
 */
export interface Correction {
  readonly veld: string
  readonly datatype: string
  readonly waarden: readonly string[]
  /** De rij waarin `veld` een kolom is; zonder rij is `veld` een veld van een sectie. */
  readonly rij?: string | undefined
  /** Bij een bijlageveld: de bestanden die het veld na de correctie draagt, op hun hash. */
  readonly bijlagen?: readonly { readonly hash: string; readonly bestandsnaam: string }[] | undefined
}

/** Wat de opslag weet van een bestand dat een correctie als bijlage noemt. */
export interface DeliveredFile {
  readonly grootteBytes: number
  /** De soort die de controle bij het aanleveren vaststelde, of niets als het bestand die controle nooit doorliep. */
  readonly soort: string | undefined
  /** Of het processysteem het bestand aanleverde voor het dossier van de instantie die wordt aangemaakt. */
  readonly voorDitDossier: boolean
}

/** Wat een correctie van een bijlage nodig heeft, van de aanroeper. */
export interface AttachmentCheck {
  /** Per hash uit `correctionAttachments`; een hash die er niet in staat, staat niet in de opslag. */
  readonly bestanden: ReadonlyMap<string, DeliveredFile>
  /**
   * De eisen van het veld (R-104) op het gecorrigeerde antwoord: een melding als het er niet aan
   * voldoet, ook als het veld geen eisen heeft, of niets als het past.
   */
  readonly veldeisen: (veld: string, answer: Answer) => string | undefined
}

const SIMPLE = new Set(['text', 'number', 'amount', 'date', 'datetime', 'email', 'url', 'phone', 'choice', 'multichoice', 'boolean'])
const CORRECTABLE = new Set([...SIMPLE, 'richtext', 'attachment'])
const DATE = /^\d{4}-\d{2}-\d{2}$/
const DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/

/** Wie een correctie op zijn naam krijgt: het processysteem, langs de koppelvlakdienst. */
export const CORRECTED_BY: Person = 'koppelvlak:processysteem'

/** De figuren in de rich-text-correcties, voor de aanroeper om in de opslag op te zoeken. */
export function correctionFigures(corrections: readonly Correction[]): string[] {
  const hashes = corrections
    .filter((correction) => correction.datatype === 'richtext' && correction.waarden.length === 1)
    .flatMap((correction) => importRichText(correction.waarden[0] ?? '').figures)
  return [...new Set(hashes)]
}

/** De bijlagen in de correcties, voor de aanroeper om in de opslag op te zoeken. */
export function correctionAttachments(corrections: readonly Correction[]): string[] {
  return [...new Set(corrections.flatMap((correction) => (correction.bijlagen ?? []).map((attachment) => attachment.hash)))]
}

type Herhaalgroep = Extract<Field, { control: 'herhaalgroep' }>
type Origin = { readonly door: Person; readonly gewijzigdOp: Moment }

/**
 * Past de correcties toe op een kopie van `responses`. `files` is wat de opslag weet van de figuren
 * uit `correctionFigures`; een figuur die er niet in staat, wordt geweigerd.
 */
export function applyCorrections(
  definitie: Definitie,
  responses: Readonly<Record<string, Answer>>,
  corrections: readonly Correction[],
  moment: Moment,
  files: ReadonlyMap<string, StoredFile> = new Map(),
  attachments?: AttachmentCheck,
): { readonly antwoorden: Record<string, Answer>; readonly problemen: readonly string[] } {
  const out: Record<string, Answer> = { ...responses }
  const problems: string[] = []
  const fields = new Map(definitie.secties.flatMap((section) => section.velden.map((field) => [field.id, field] as const)))
  const groupOf = new Map<string, Herhaalgroep>()
  for (const field of fields.values()) {
    if (field.control === 'herhaalgroep') for (const column of field.rijVelden) groupOf.set(column.id, field)
  }
  const origin: Origin = { door: CORRECTED_BY, gewijzigdOp: moment }

  for (const correction of corrections) {
    const row = correction.rij
    const where = row === undefined ? `correctie op ${correction.veld}` : `correctie op ${correction.veld} in rij ${row}`
    const group = groupOf.get(correction.veld)

    if (row === undefined) {
      const field = fields.get(correction.veld)
      if (field === undefined) {
        problems.push(
          group === undefined
            ? `${where}: het formulier kent dit veld niet`
            : `${where}: dit is een kolom van herhaalgroep ${group.id}; een correctie erop noemt de rij`,
        )
        continue
      }
      if (field.control === 'herhaalgroep') {
        problems.push(`${where}: een herhaalgroep wordt per cel gecorrigeerd, met de rij en de kolom als veld`)
        continue
      }
      const value = correctedValue(field, correction, origin, files, attachments)
      if (typeof value === 'string') problems.push(`${where}: ${value}`)
      else out[correction.veld] = value
      continue
    }

    if (group === undefined) {
      problems.push(
        fields.has(correction.veld)
          ? `${where}: dit veld is geen kolom van een herhaalgroep, en heeft dus geen rij`
          : `${where}: het formulier kent dit veld niet`,
      )
      continue
    }
    const current = out[group.id]
    const rows = current !== undefined && 'rijen' in current ? current.rijen : []
    const index = rows.findIndex((candidate) => candidate.rijId === row)
    if (index === -1) {
      problems.push(`${where}: herhaalgroep ${group.id} heeft in deze stand geen rij ${row}`)
      continue
    }
    const column = group.rijVelden.find((candidate) => candidate.id === correction.veld)
    const value = column === undefined ? 'het formulier kent dit veld niet' : correctedValue(column, correction, origin, files, attachments)
    if (typeof value === 'string') {
      problems.push(`${where}: ${value}`)
      continue
    }
    out[group.id] = Answer.parse({
      rijen: rows.map((candidate, i) => (i === index ? { ...candidate, waarden: { ...candidate.waarden, [correction.veld]: value } } : candidate)),
    })
  }
  return { antwoorden: out, problemen: problems }
}

/** Het gecorrigeerde antwoord van één veld of één cel, of wat er niet aan past. */
function correctedValue(
  field: Field | RowField,
  correction: Correction,
  origin: Origin,
  files: ReadonlyMap<string, StoredFile>,
  attachments: AttachmentCheck | undefined,
): Answer | string {
  const datatype = datatypeOf(field)
  if (datatype === undefined || !CORRECTABLE.has(datatype)) return `een veld van datatype ${datatype ?? 'zonder antwoord'} is nog niet te corrigeren`
  if (correction.datatype !== datatype) return `het veld is ${datatype}, de correctie zegt ${correction.datatype}`
  if (datatype === 'attachment') return attachmentAnswer(field.id, correction, origin, attachments)
  if (correction.bijlagen !== undefined) return 'alleen een bijlageveld draagt bijlagen'
  const options = 'opties' in field && field.opties !== undefined ? new Set(field.opties.map((o) => o.waarde)) : undefined
  if (datatype === 'multichoice') {
    const unknown = correction.waarden.filter((value) => options !== undefined && !options.has(value))
    if (unknown.length > 0) return `${unknown.join(', ')} is geen keuze van dit veld`
    return Answer.parse({ ...origin, waarden: [...new Set(correction.waarden)] })
  }
  if (correction.waarden.length !== 1) return `verwacht precies één waarde, kreeg er ${correction.waarden.length}`
  const text = correction.waarden[0] ?? ''
  if (datatype === 'richtext') return richTextAnswer(text, origin, files)
  let value: string | number | boolean = text
  if (datatype === 'number' || datatype === 'amount') {
    value = Number(text)
    if (text.trim() === '' || !Number.isFinite(value)) return `${text} is geen getal`
  } else if (datatype === 'boolean') {
    if (text !== 'true' && text !== 'false') return `${text} is geen true of false`
    value = text === 'true'
  } else if (datatype === 'date' && !DATE.test(text)) {
    return `${text} is geen datum`
  } else if (datatype === 'datetime' && !DATETIME.test(text)) {
    return `${text} is geen tijdstip in UTC`
  } else if (datatype === 'choice' && options !== undefined && !options.has(text)) {
    return `${text} is geen keuze van dit veld`
  }
  return Answer.parse({ ...origin, waarde: value })
}

/**
 * HTML naar een rich-text-antwoord: de structuur van de editor met de platte tekst ernaast.
 *
 * HTML zonder tekst wordt geweigerd. De pod laat een rich-text-veld zonder tekst uit de stand
 * (`answerOf` in de renderer), ook als er een figuur in staat; een correctie die zo'n antwoord
 * schreef, zou in de verwijzing iets tonen dat het formulier zelf nooit als antwoord heeft.
 */
function richTextAnswer(html: string, origin: Origin, files: ReadonlyMap<string, StoredFile>): Answer | string {
  const imported = importRichText(html)
  if (imported.structure === undefined) return imported.problems.join('; ')
  const figures = checkFigures(imported.figures, files)
  if (figures.problems.length > 0) return figures.problems.join('; ')
  const structure = withFigureSizes(imported.structure, figures.sizes)
  const text = plainTextOfStructure(structure)
  if (text.trim() === '') return 'de HTML heeft geen tekst, en een rich-text-veld zonder tekst is op het formulier geen antwoord'
  return Answer.parse({ ...origin, waarde: text, structuur: structure })
}

/**
 * De bijlagen van een correctie als verwijzingen (U25, R-199), of wat er niet aan past.
 *
 * Elk bestand moet in de opslag staan, bij het aanleveren de controle van de opslagdienst hebben
 * doorstaan met een soort die nu nog op de lijst staat, en voor dit dossier zijn aangeleverd: een hash
 * uit een ander dossier noemen hangt geen bestand van een ander in deze stand. De bestandsnaam moet bij
 * die soort passen. De grootte komt uit de opslag en niet uit het verzoek. Daarna de eisen van het veld,
 * dezelfde die het formulier toetst (R-104). Een lege lijst wordt geweigerd: alle bijlagen weghalen is
 * geen correctie van een waarde, en een verplicht bijlageveld (R-106) zou er een stand van maken die het
 * formulier zelf niet laat indienen.
 */
function attachmentAnswer(veld: string, correction: Correction, origin: Origin, check: AttachmentCheck | undefined): Answer | string {
  if (check === undefined) return 'een bijlage is zonder de controle van de opslag niet te corrigeren'
  const named = correction.bijlagen ?? []
  if (named.length === 0) return 'een correctie van een bijlageveld noemt minstens één bijlage'
  if (correction.waarden.length > 0) return 'een bijlage noemt haar bestand met een bijlage op haar hash, niet met een waarde'
  const problems: string[] = []
  const documents: DocumentVerwijzing[] = []
  const seen = new Set<string>()
  for (const { hash, bestandsnaam } of named) {
    if (seen.has(hash)) {
      problems.push(`de bijlage ${hash} staat er twee keer in`)
      continue
    }
    seen.add(hash)
    const file = check.bestanden.get(hash)
    if (file === undefined) {
      problems.push(`de bijlage ${hash} staat niet in de opslag; lever haar eerst aan met POST /bijlagen en het dossier`)
      continue
    }
    if (file.soort === undefined || !isFileKind(file.soort)) {
      problems.push(`de bijlage ${hash} is niet als bijlage aangeleverd en dus niet gecontroleerd; lever haar aan met POST /bijlagen en het dossier`)
      continue
    }
    if (!file.voorDitDossier) {
      problems.push(`de bijlage ${hash} is niet voor dit dossier aangeleverd`)
      continue
    }
    const reference = DocumentVerwijzing.safeParse({ hash, bestandsnaam, grootteBytes: file.grootteBytes })
    if (!reference.success) {
      problems.push(`${bestandsnaam} is een pad en geen bestandsnaam`)
      continue
    }
    if (!fitsFileKind(bestandsnaam, file.soort)) {
      problems.push(`de bestandsnaam ${bestandsnaam} past niet bij de inhoud van ${hash}, en die is ${file.soort}`)
      continue
    }
    documents.push(reference.data)
  }
  if (problems.length > 0) return problems.join('; ')
  const answer = Answer.parse({ ...origin, documenten: documents })
  return check.veldeisen(veld, answer) ?? answer
}
